import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { WorkoutView } from '../../types';
import { useTranslation } from '../../i18n/provider';
import RestTimer from '../common/RestTimer';
import styles from './Navigation.module.css';

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const activeTab = React.useMemo((): string => {
    const p = location.pathname;
    if (p.startsWith('/exercises')) return 'exercises';
    if (p.startsWith('/progress')) return 'progress';
    if (p.startsWith('/food')) return 'food';
    if (p.startsWith('/feedback')) return 'feedback';
    if (p.startsWith('/about')) return 'about';
    if (p.startsWith('/workouts')) return 'workouts';
    return 'workouts';
  }, [location.pathname]);

  const workoutView = React.useMemo<WorkoutView>(() => {
    const p = location.pathname;
    if (!p.startsWith('/workouts')) {
      return 'today';
    }
    const view = p.split('/')[2];
    if (view === 'today' || view === 'history' || view === 'templates') {
      return view;
    }
    return 'today';
  }, [location.pathname]);

  const tabToPath = React.useCallback((tab: string): string => {
    switch (tab) {
      case 'exercises':
        return '/exercises';
      case 'progress':
        return '/progress';
      case 'food':
        return '/food';
      case 'feedback':
        return '/feedback';
      case 'about':
        return '/about';
      case 'workouts':
      default:
        return '/workouts/today';
    }
  }, []);

  const handleTabChange = (tabId: string) => {
    const nextPath = tabToPath(tabId);
    if (location.pathname !== nextPath) {
      navigate(nextPath);
    }
  };

  // Timer state
  const [restDuration, setRestDuration] = useState<number>(10);
  const [restLeft, setRestLeft] = useState<number | null>(10);
  const [restTimerId, setRestTimerId] = useState<number | null>(null);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  const [showTimerAlert, setShowTimerAlert] = useState<boolean>(false);
  const [isTimerMinimized, setIsTimerMinimized] = useState<boolean>(true);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const startRest = () => {
    setHasStarted(true);
    const id = window.setInterval(() => {
      setRestLeft(prev => {
        if (prev === null) return prev;
        if (prev <= 1) {
          window.clearInterval(id);
          setRestTimerId(null);
          setShowTimerAlert(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setRestTimerId(id);
  };

  const pauseRest = () => {
    if (restTimerId) {
      window.clearInterval(restTimerId);
      setRestTimerId(null);
    }
  };

  const resetRest = () => {
    if (restTimerId) {
      window.clearInterval(restTimerId);
      setRestTimerId(null);
    }
    setRestLeft(restDuration);
    setHasStarted(false);
  };

  const setPreset = (seconds: number) => {
    if (restTimerId) {
      window.clearInterval(restTimerId);
      setRestTimerId(null);
    }
    setRestDuration(seconds);
    setRestLeft(seconds);
    setHasStarted(false);
  };

  const handleTimerComplete = () => {
    setShowTimerAlert(false);
    setRestLeft(restDuration);
    setHasStarted(false);
  };

  const navItems = [
    { id: 'workouts', icon: '🏋️', label: t('navigation.tabs.workouts') },
    { id: 'exercises', icon: '💪', label: t('navigation.tabs.exercises') },
    { id: 'progress', icon: '📊', label: t('navigation.tabs.progress') },
    { id: 'food', icon: '🥗', label: t('navigation.tabs.food') },
    { id: 'feedback', icon: '💬', label: t('navigation.tabs.feedback') },
    { id: 'about', icon: '?', label: t('navigation.tabs.about') },
  ];

  const renderSubNavigation = () => {
    if (activeTab !== 'workouts') return null;

    return (
      <div className="sub-nav">
        <button
          className={`sub-nav-button ${workoutView === 'today' ? 'active' : ''}`}
          onClick={() => navigate('/workouts/today')}
        >
          {t('navigation.workoutViews.today')}
        </button>
        <button
          className={`sub-nav-button ${workoutView === 'history' ? 'active' : ''}`}
          onClick={() => navigate('/workouts/history')}
        >
          {t('navigation.workoutViews.history')}
        </button>
        <button
          className={`sub-nav-button ${workoutView === 'templates' ? 'active' : ''}`}
          onClick={() => navigate('/workouts/templates')}
        >
          {t('navigation.workoutViews.templates')}
        </button>
      </div>
    );
  };

  return (
    <div className="nav-container" data-testid="workout-menu" style={{ position: 'relative' }}>
      {!isTimerMinimized ? (
        <RestTimer
          label={formatTime(restLeft ?? restDuration)}
          onStart={startRest}
          onPause={pauseRest}
          onReset={resetRest}
          onPreset={setPreset}
          canPause={restTimerId !== null}
          hasStarted={hasStarted}
          onMinimize={restTimerId === null ? () => setIsTimerMinimized(true) : undefined}
        />
      ) : (
        <div className={styles.minimizedTimer}>
          <span className={styles.minimizedText}>
            {t('navigation.timer.hint')}
          </span>
          <button 
            onClick={() => setIsTimerMinimized(false)}
            className={styles.minimizedButton}
            title={t('navigation.timer.show')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 3 21 3 21 9"></polyline>
              <polyline points="9 21 3 21 3 15"></polyline>
              <line x1="21" y1="3" x2="14" y2="10"></line>
              <line x1="3" y1="21" x2="10" y2="14"></line>
            </svg>
          </button>
        </div>
      )}
      <div className="nav-tabs">
        {navItems.map((item: { id: string; icon: string; label: string }) => (
          <div 
            key={item.id}
            className={`nav-tab ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => handleTabChange(item.id)}
            style={{ position: 'relative' }}
          >
            <span className="nav-icon">{item.icon}</span>
            {item.label}
          </div>
        ))}
      </div>
      {renderSubNavigation()}

      {showTimerAlert && (
        <div 
          onClick={handleTimerComplete}
          className={styles.timerAlert}
        >
          <div className={styles.timerAlertText}>
            {t('navigation.timer.alert')}
          </div>
        </div>
      )}
    </div>
  );
};

export default Navigation;
